import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { X, Settings2, Eye, EyeOff, Trash2, ExternalLink, GripVertical, Zap, Loader2, CheckCircle, Copy, Play, Search, Terminal, RefreshCw, Clock, ChevronRight, ChevronDown, List as ListIcon, Code, Plus, GitBranch, GitFork, ToggleLeft, ToggleRight, Check } from 'lucide-react';
import {
  listCredentials,
  listMarketplaceAgents,
  startWebhookListen,
  stopWebhookListen,
  getWebhookListenStatus,
  injectWebhookPayload,
  getWebhookSchema
} from '../../lib/workflowApi';
import { useTheme } from '../../contexts/ThemeContext';

const INPUT_TYPES = {
  text: 'text',
  textarea: 'textarea',
  number: 'number',
  select: 'select',
  boolean: 'boolean',
  credential: 'credential',
  agent_select: 'agent_select',
};

// Static map of node type → output fields (mirrors actual node return dicts in the backend).
// Used by the Upstream Variables picker so users can copy {{node_id.field}} templates.
const NODE_OUTPUT_FIELDS = {
  ManualTrigger:      [],
  WebhookTrigger:     ['body', 'headers', 'query'],
  ScheduleTrigger:    [],
  OpenAI:             ['response', 'model', 'usage.prompt_tokens', 'usage.completion_tokens', 'usage.total_tokens'],
  MarketplaceAgent:   ['response', 'agent_id', 'agent_name'],
  AITransform:        ['transformed'],
  TextSummarizer:     ['summary', 'style', 'original_length', 'summary_length'],
  SentimentAnalysis:  ['sentiment', 'confidence', 'emotions', 'text_preview'],
  SendEmail:          ['sent', 'to', 'subject'],
  SlackNotification:  ['sent', 'channel', 'ts'],
  PostgreSQLQuery:    ['rows', 'row_count'],
  MySQLQuery:         ['rows', 'row_count'],
  HttpRequest:        ['status_code', 'body', 'headers'],
  WebScraper:         ['url', 'status_code', 'text', 'content', 'links'],
  WebSearch:          ['query', 'results', 'text', 'response'],
  IfCondition:        ['result', 'matched_handle'],
  SwitchCase:         ['value', 'matched_case', 'matched_cases'],
  SetVariable:        ['value', 'variable_name'],
  JSONTransform:      ['result'],
  DelayTimer:         ['waited_seconds'],
  CodeExecutor:       ['result', 'stdout', 'stderr', 'exit_code'],
  DataValidator:      ['validated', 'rules_checked', 'errors'],
  AutonomousNode:     ['response', 'steps', 'tool_results'],
  RAGSearch:          ['query', 'category', 'chunks', 'chunk_count', 'text', 'response'],
};

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components for Schema Tree
// ─────────────────────────────────────────────────────────────────────────────

const TypeBadge = ({ type }) => {
  const colors = {
    object: { bg: 'rgba(59, 130, 246, 0.1)', text: '#3B82F6' },
    array: { bg: 'rgba(168, 85, 247, 0.1)', text: '#A855F7' },
    string: { bg: 'rgba(16, 185, 129, 0.1)', text: '#10B981' },
    number: { bg: 'rgba(245, 158, 11, 0.1)', text: '#F59E0B' },
    boolean: { bg: 'rgba(239, 68, 68, 0.1)', text: '#EF4444' },
    null: { bg: 'rgba(107, 114, 128, 0.1)', text: '#6B7280' },
  };
  const theme = colors[type] || colors.string;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700, textTransform: 'uppercase', padding: '1px 5px',
      borderRadius: 4, background: theme.bg, color: theme.text, letterSpacing: '0.02em'
    }}>
      {type}
    </span>
  );
};

const SchemaRow = ({ node, level, onCopy, searchQuery, isOpen, onToggle }) => {
  const hasChildren = node.children && node.children.length > 0;
  const matches = searchQuery ? node.path.toLowerCase().includes(searchQuery.toLowerCase()) : true;

  if (searchQuery && !matches) return null;

  return (
    <div
      className="schema-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
        borderRadius: 6, cursor: 'default', transition: 'background 0.2s',
        fontSize: 12, position: 'relative', minHeight: 28,
        marginLeft: level * 12
      }}
    >
      {hasChildren ? (
        <button
          onClick={onToggle}
          style={{
            background: 'none', border: 'none', padding: 0, display: 'flex',
            alignItems: 'center', cursor: 'pointer', color: '#64748b'
          }}
        >
          {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
      ) : (
        <div style={{ width: 14 }} />
      )}

      <span style={{
        color: '#E2E8F0', fontWeight: 500,
        fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
        fontSize: 11
      }}>
        {node.path.split('.').pop().replace(/\[\d+\]$/, '')}
        {node.path.endsWith(']') && <span style={{ color: '#64748B' }}>[0]</span>}
      </span>

      <TypeBadge type={node.type} />

      {node.type !== 'object' && node.type !== 'array' && (
        <span
          title={String(node.value)}
          style={{
            color: '#64748B', fontSize: 11, fontStyle: 'italic',
            whiteSpace: 'nowrap', overflow: 'hidden',
            maskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 70%, transparent 100%)',
            maxWidth: 160, marginLeft: 8, opacity: 0.7
          }}
        >
          {String(node.value)}
        </span>
      )}

      <div style={{ flex: 1 }} />

      {/* Copy Button */}
      <button
        className="copy-btn"
        onClick={() => onCopy(node.template)}
        title="Copy field path"
        style={{
          background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
          padding: '3px 6px', borderRadius: 4, color: '#94A3B8', cursor: 'pointer',
          display: 'flex', alignItems: 'center', transition: 'all 0.2s',
        }}
      >
        <Copy size={11} />
      </button>
    </div>
  );
};

const SchemaTree = ({ schema, onCopy, searchQuery }) => {
  const [expandedPaths, setExpandedPaths] = useState(new Set());

  const matchesSearch = (node, query) => {
    if (!query) return true;
    const lowerQuery = query.toLowerCase();
    if (node.path.toLowerCase().includes(lowerQuery)) return true;
    if (node.children) {
      return node.children.some(child => matchesSearch(child, query));
    }
    return false;
  };

  // const renderNodes = (nodes, level = 0) => {
  //   return nodes.map((node, idx) => {
  //     if (searchQuery && !matchesSearch(node, searchQuery)) return null;

  //     // Auto-expand if searching and this node or children match
  //     const isActuallyOpen = expandedPaths.has(node.path) ||
  //       (level === 0 && !searchQuery && !expandedPaths.has("__closed_" + node.path)) ||
  //       (searchQuery && matchesSearch(node, searchQuery));

  //     return (
  //       <React.Fragment key={node.path || idx}>
  //         <SchemaRow
  //           node={node}
  //           level={level}
  //           onCopy={onCopy}
  //           searchQuery={searchQuery}
  //           isOpen={isActuallyOpen}
  //           onToggle={() => {
  //             const closedKey = "__closed_" + node.path;
  //             const newSet = new Set(expandedPaths);
  //             if (level === 0) {
  //               if (newSet.has(closedKey)) newSet.delete(closedKey);
  //               else newSet.add(closedKey);
  //             } else {
  //               if (newSet.has(node.path)) newSet.delete(node.path);
  //               else newSet.add(node.path);
  //             }
  //             setExpandedPaths(newSet);
  //           }}
  //         />
  //         {node.children && node.children.length > 0 && isActuallyOpen && (
  //           <div style={{ borderLeft: '1px solid rgba(255,255,255,0.05)', marginLeft: 6 }}>
  //             {renderNodes(node.children, level + 1)}
  //           </div>
  //         )}
  //       </React.Fragment>
  //     );
  //   });
  // };
    const renderNodes = (nodes, level = 0) => {
      const flatNodes = [];
      nodes.forEach(node => {
        if (node.path === 'body' && node.children) {
          flatNodes.push(...node.children);
        } else {
          flatNodes.push(node);
        }
      });

      return flatNodes.map((node, idx) => {
        if (searchQuery && !matchesSearch(node, searchQuery)) return null;

        // Auto-expand if searching and this node or children match
        const isActuallyOpen = expandedPaths.has(node.path) ||
          (level === 0 && !searchQuery && !expandedPaths.has("__closed_" + node.path)) ||
          (searchQuery && matchesSearch(node, searchQuery));

        return (
          <React.Fragment key={node.path || idx}>
            <SchemaRow
              node={node}
              level={level}
              onCopy={onCopy}
              searchQuery={searchQuery}
              isOpen={isActuallyOpen}
              onToggle={() => {
                const closedKey = "__closed_" + node.path;
                const newSet = new Set(expandedPaths);
                if (level === 0) {
                  if (newSet.has(closedKey)) newSet.delete(closedKey);
                  else newSet.add(closedKey);
                } else {
                  if (newSet.has(node.path)) newSet.delete(node.path);
                  else newSet.add(node.path);
                }
                setExpandedPaths(newSet);
              }}
            />
            {node.children && node.children.length > 0 && isActuallyOpen && (
              <div style={{ borderLeft: '1px solid rgba(255,255,255,0.05)', marginLeft: 6 }}>
                {renderNodes(node.children, level + 1)}
              </div>
            )}
          </React.Fragment>
        );
      });
    };

  return <div className="schema-tree" style={{ userSelect: 'none' }}>{renderNodes(schema)}</div>;
};

function Field({ field, value, onChange, onChangeAgent, credentials, agents = [], onManageCredentials }) {
  const [showSecret, setShowSecret] = useState(false);
  const { isDark } = useTheme();

  const isSecret = field.name.toLowerCase().includes('key') ||
    field.name.toLowerCase().includes('secret') ||
    field.name.toLowerCase().includes('password') ||
    field.name.toLowerCase().includes('token');

  const inputStyle = {
    width: '100%',
    background: isDark ? '#161B22' : '#ffffff',
    border: `1px solid ${isDark ? '#30363D' : '#cbd5e1'}`,
    borderRadius: 7,
    padding: '7px 10px',
    color: isDark ? '#E6EDF3' : '#0f172a',
    fontSize: 12,
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: "'Inter', sans-serif",
    colorScheme: isDark ? 'dark' : 'light',
  };

  if (field.type === 'textarea') {
    return (
      <textarea
        style={{ ...inputStyle, minHeight: 80, resize: 'vertical', lineHeight: 1.6 }}
        value={value || ''}
        placeholder={field.placeholder || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
      />
    );
  }

  if (field.type === 'select') {
    return (
      <select
        style={{ ...inputStyle, cursor: 'pointer' }}
        value={value || field.options?.[0] || ''}
        onChange={(e) => onChange(field.name, e.target.value)}
      >
        {(field.options || []).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    );
  }

  if (field.type === 'boolean') {
    return (
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(field.name, e.target.checked)}
          style={{ accentColor: '#8B5CF6', width: 15, height: 15 }}
        />
        <span style={{ fontSize: 12, color: isDark ? '#94A3B8' : '#64748b' }}>
          {value ? 'Enabled' : 'Disabled'}
        </span>
      </label>
    );
  }

  if (field.type === 'credential') {
    const credType = field.credential_type;
    const filtered = credentials.filter(
      (c) => !credType || c.type === credType
    );
    return (
      <div>
        <select
          style={{ ...inputStyle, cursor: 'pointer' }}
          value={value || ''}
          onChange={(e) => onChange(field.name, e.target.value)}
        >
          <option value="">— Select credential —</option>
          {filtered.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} ({c.type})
            </option>
          ))}
        </select>
        {filtered.length === 0 && (
          <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 10, color: '#EF4444' }}>No {credType} credentials found.</span>
            <button
              type="button"
              onClick={onManageCredentials}
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, fontSize: 10, color: '#F87217', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 2 }}
            >
              Manage <ExternalLink size={10} />
            </button>
          </div>
        )}
      </div>
    );
  }

  if (field.type === 'agent_select') {
    const selectedAgent = agents.find((a) => a.id === value);
    return (
      <div>
        <select
          style={{ ...inputStyle, cursor: 'pointer' }}
          value={value || ''}
          onChange={(e) => {
            const agentId = e.target.value;
            const agent = agents.find((a) => a.id === agentId);
            if (onChangeAgent) {
              onChangeAgent(field.name, agentId, agent?.name || '');
            } else {
              onChange(field.name, agentId);
            }
          }}
        >
          <option value="">— Select Agent —</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name} {a.is_custom ? '(Custom)' : ''}
            </option>
          ))}
        </select>
        {selectedAgent && (
          <div
            style={{
              marginTop: 8,
              padding: '7px 10px',
              background: isDark ? 'rgba(6, 182, 212, 0.08)' : 'rgba(6, 182, 212, 0.05)',
              border: `1px solid ${isDark ? 'rgba(6, 182, 212, 0.25)' : 'rgba(6, 182, 212, 0.15)'}`,
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: '#06B6D4',
                flexShrink: 0,
                boxShadow: '0 0 6px rgba(6,182,212,0.6)',
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#06B6D4', marginBottom: 1 }}>
                {selectedAgent.name}
              </div>
              {selectedAgent.is_custom && (
                <div style={{ fontSize: 9, color: isDark ? '#64748B' : '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Custom Agent
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        style={{
          ...inputStyle,
          paddingRight: isSecret ? 34 : 10,
          fontFamily: isSecret && !showSecret ? 'monospace' : "'Inter', sans-serif",
        }}
        type={field.type === 'number' ? 'number' : isSecret && !showSecret ? 'password' : 'text'}
        value={value ?? (field.default ?? '')}
        placeholder={field.placeholder || ''}
        onChange={(e) =>
          onChange(
            field.name,
            field.type === 'number' ? parseFloat(e.target.value) : e.target.value
          )
        }
      />
      {isSecret && (
        <button
          style={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: isDark ? '#64748B' : '#64748b',
            padding: 0,
          }}
          onClick={() => setShowSecret((v) => !v)}
          type="button"
        >
          {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Webhook Discovery Panel
// ─────────────────────────────────────────────────────────────────────────────

const WebhookDiscoveryPanel = ({ node, workflowId, onClose, onDelete, onMouseDownResize }) => {
  const { isDark } = useTheme();

  // Theme colors
  const panelBg = isDark ? '#0D1117' : '#f8fafc';
  const panelBorder = isDark ? '#30363D' : '#e2e8f0';
  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#94A3B8' : '#64748b';
  const inputBg = isDark ? '#010409' : '#ffffff';
  const inputBorder = isDark ? '#30363D' : '#cbd5e1';

  // Listen State
  const [isListening, setIsListening] = useState(false);
  const [countdown, setCountdown] = useState(300);
  const [listenStatus, setListenStatus] = useState('none'); // none, waiting, captured, expired
  const [schema, setSchema] = useState(null);
  const [capturedPayload, setCapturedPayload] = useState(null);

  // UI State
  const [activeTab, setActiveTab] = useState('schema'); // schema, raw, inject
  const [searchQuery, setSearchQuery] = useState('');
  const [copySuccess, setCopySuccess] = useState('');
  const [injectPayload, setInjectPayload] = useState('{\n  "event": "user.signup",\n  "user": {\n    "id": "usr_01",\n    "email": "test@example.com"\n  }\n}');
  const [isInjecting, setIsInjecting] = useState(false);

  const timerRef = useRef(null);
  const pollRef = useRef(null);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleCopy = (template) => {
    navigator.clipboard.writeText(template);
    setCopySuccess(template);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const startListen = async () => {
    try {
      const res = await startWebhookListen(workflowId);
      setIsListening(true);
      setCountdown(300);
      setListenStatus('waiting');
      setCapturedPayload(null);
      setSchema(null);

      // Start polling
      pollRef.current = setInterval(checkStatus, 3000);
      // Start countdown
      timerRef.current = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            stopListen();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Failed to start listen:", err);
    }
  };

  const stopListen = async () => {
    clearInterval(pollRef.current);
    clearInterval(timerRef.current);
    setIsListening(false);
    try {
      await stopWebhookListen(workflowId);
    } catch (err) { }
  };

  const checkStatus = async () => {
    try {
      const res = await getWebhookListenStatus(workflowId);
      if (res.data.status === 'captured') {
        setListenStatus('captured');
        setCapturedPayload(res.data.payload);
        setSchema(res.data.schema);
        stopListen();
      } else if (res.data.status === 'expired') {
        setListenStatus('expired');
        stopListen();
      }
    } catch (err) {
      console.error("Polling error:", err);
    }
  };

  const handleInject = async () => {
    let body;
    try {
      body = JSON.parse(injectPayload);
    } catch (e) {
      alert("Invalid JSON");
      return;
    }

    setIsInjecting(true);
    try {
      const res = await injectWebhookPayload(workflowId, body);
      setCapturedPayload(res.data.payload);
      setSchema(res.data.schema);
      setListenStatus('captured');
      setActiveTab('schema');
    } catch (err) {
      console.error("Injection error:", err);
    } finally {
      setIsInjecting(false);
    }
  };

  const fetchExistingSchema = async () => {
    try {
      const res = await getWebhookSchema(workflowId);
      if (res.data && res.data.fields) {
        setSchema(res.data.fields);
        setCapturedPayload(res.data.raw_sample);
        setListenStatus('captured');
      }
    } catch (err) {
      // No schema yet, that's fine
    }
  };

  useEffect(() => {
    if (workflowId && workflowId !== 'new') {
      fetchExistingSchema();
    }
    return () => {
      clearInterval(pollRef.current);
      clearInterval(timerRef.current);
    };
  }, [workflowId]);

  return (
    <div style={{
      width: 420, flexShrink: 0, background: panelBg, borderLeft: `1px solid ${panelBorder}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', fontFamily: "'Inter', sans-serif", position: 'relative'
    }}>
      {/* Resize handle */}
      <div
        style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 6, cursor: 'ew-resize',
          zIndex: 10, background: 'transparent'
        }}
        onMouseDown={onMouseDownResize}
      />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${panelBorder}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Zap size={15} color="#F59E0B" fill="#F59E0B" />
          <span style={{ fontSize: 13, fontWeight: 700, color: text1 }}>Webhook Discovery</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onDelete && (
            <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }} onClick={() => onDelete(node.id)}>
              <Trash2 size={14} />
            </button>
          )}
          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, padding: 4 }} onClick={onClose}>
            <X size={15} />
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

        {/* Listen Mode Control */}
        <div style={{ padding: 16, borderBottom: `1px solid ${panelBorder}`, background: isListening ? 'rgba(245, 158, 11, 0.03)' : 'transparent' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: text1, marginBottom: 4 }}>Listen for Events</div>
              <div style={{ fontSize: 11, color: text2, lineHeight: 1.4 }}>
                Capture real payload data to discover fields automatically.
              </div>
            </div>
            {isListening ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#F59E0B', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div className="pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: '#F59E0B' }} />
                  {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, '0')}
                </div>
                <button
                  onClick={stopListen}
                  style={{ fontSize: 10, color: '#EF4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={startListen}
                style={{
                  background: '#F59E0B', color: 'white', border: 'none', padding: '6px 12px',
                  borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6
                }}
              >
                <Clock size={12} />
                Listen
              </button>
            )}
          </div>

          {isListening && (
            <div style={{
              background: isDark ? '#161B22' : '#f1f5f9', padding: 8, borderRadius: 6,
              fontSize: 10, color: '#F59E0B', fontFamily: 'monospace', border: '1px dashed #F59E0B'
            }}>
              Waiting for POST to your webhook URL...
            </div>
          )}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${panelBorder}`, background: isDark ? '#0D1117' : '#f8fafc' }}>
          {[
            { id: 'schema', label: 'Variable Picker', icon: <ListIcon size={12} /> },
            { id: 'raw', label: 'Raw JSON', icon: <Code size={12} /> },
            { id: 'inject', label: 'Send Test', icon: <Terminal size={12} /> },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1, padding: '10px 0', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 11, fontWeight: activeTab === tab.id ? 700 : 500,
                color: activeTab === tab.id ? '#F59E0B' : text2,
                borderBottom: `2px solid ${activeTab === tab.id ? '#F59E0B' : 'transparent'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'all 0.2s'
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>

          {activeTab === 'schema' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {/* Header: Search + Refresh (Always Visible) */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Search style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} size={14} />
                  <input
                    placeholder="Search fields..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{
                      width: '100%', background: inputBg, border: `1px solid ${inputBorder}`,
                      borderRadius: 6, padding: '6px 10px 6px 28px', color: text1, fontSize: 12,
                      outline: 'none', boxSizing: 'border-box'
                    }}
                  />
                </div>
                <button
                  onClick={fetchExistingSchema}
                  title="Refresh from last execution log"
                  style={{
                    background: 'none', border: `1px solid ${panelBorder}`,
                    borderRadius: 6, padding: '6px', color: text2, cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}
                >
                  <RefreshCw size={14} />
                </button>
              </div>

              {listenStatus === 'captured' && schema ? (
                <SchemaTree
                  schema={(schema || []).filter(s => s.path === 'body')}
                  onCopy={handleCopy}
                  searchQuery={searchQuery}
                />
              ) : (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: text2 }}>
                  <Zap size={32} color="#F59E0B" style={{ opacity: 0.2, marginBottom: 12 }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: text1, marginBottom: 4 }}>No Data Captured</div>
                  <div style={{ fontSize: 11, lineHeight: 1.5 }}>
                    Click <strong>Listen</strong> and fire your webhook, or use <strong>Send Test</strong> to inject a sample payload.
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'raw' && (
            <div style={{ height: '100%', position: 'relative' }}>
              {capturedPayload ? (
                <>
                  <button
                    onClick={() => handleCopy(JSON.stringify(capturedPayload.body || capturedPayload, null, 2))}
                    style={{
                      position: 'absolute', top: 8, right: 8, background: 'rgba(16, 185, 129, 0.1)',
                      border: '1px solid rgba(16, 185, 129, 0.2)', borderRadius: 6, color: '#10B981',
                      padding: '4px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 4, zIndex: 5
                    }}
                  >
                    <Copy size={11} />
                    Copy JSON
                  </button>
                  <pre style={{
                    margin: 0, padding: 12, background: isDark ? '#010409' : '#f1f5f9',
                    borderRadius: 8, color: '#10B981', fontSize: 11, overflow: 'auto',
                    border: `1px solid ${panelBorder}`, height: '100%', boxSizing: 'border-box'
                  }}>
                    {JSON.stringify(capturedPayload.body || capturedPayload, null, 2)}
                  </pre>
                </>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: text2 }}>No payload captured.</div>
              )}
            </div>
          )}

          {activeTab === 'inject' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, height: '100%' }}>
              <div style={{ fontSize: 11, color: text2, lineHeight: 1.5 }}>
                Paste a sample JSON payload here to manually build the schema tree and discover variables.
              </div>
              <textarea
                value={injectPayload}
                onChange={e => setInjectPayload(e.target.value)}
                style={{
                  flex: 1, width: '100%', background: inputBg, border: `1px solid ${inputBorder}`,
                  borderRadius: 8, padding: 12, color: text1, fontSize: 12,
                  fontFamily: "'JetBrains Mono', monospace", outline: 'none', resize: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={handleInject}
                disabled={isInjecting}
                style={{
                  background: '#F59E0B', color: 'white', border: 'none', padding: '10px',
                  borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
                }}
              >
                {isInjecting ? <Loader2 size={16} className="spin" /> : <Play size={14} />}
                {isInjecting ? 'Parsing...' : 'Parse & Discover'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Copy Success Toast */}
      {copySuccess && (
        <div style={{
          position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
          background: '#10B981', color: 'white', padding: '8px 16px', borderRadius: 20,
          fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8,
          boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)', zIndex: 100
        }}>
          <CheckCircle size={14} />
          Copied to clipboard
        </div>
      )}
    </div>
  );
};


// ─────────────────────────────────────────────────────────────────────────────
// IfCondition dedicated config UI
// ─────────────────────────────────────────────────────────────────────────────

const CONDITION_OPERATORS = [
  { value: 'equals', label: 'equals', unary: false },
  { value: 'not_equals', label: 'not equals', unary: false },
  { value: 'contains', label: 'contains', unary: false },
  { value: 'not_contains', label: 'not contains', unary: false },
  { value: 'starts_with', label: 'starts with', unary: false },
  { value: 'ends_with', label: 'ends with', unary: false },
  { value: 'greater_than', label: '>', unary: false },
  { value: 'greater_than_or_equal', label: '>=', unary: false },
  { value: 'less_than', label: '<', unary: false },
  { value: 'less_than_or_equal', label: '<=', unary: false },
  { value: 'regex_match', label: 'matches regex', unary: false },
  { value: 'in_list', label: 'in list', unary: false },
  { value: 'not_in_list', label: 'not in list', unary: false },
  { value: 'is_empty', label: 'is empty', unary: true },
  { value: 'is_not_empty', label: 'is not empty', unary: true },
  { value: 'is_null', label: 'is null', unary: true },
  { value: 'is_not_null', label: 'is not null', unary: true },
];

function IfConditionConfig({ config, onUpdate }) {
  const { isDark } = useTheme();

  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#94A3B8' : '#64748b';
  const inputBg = isDark ? '#161B22' : '#ffffff';
  const inputBorder = isDark ? '#30363D' : '#cbd5e1';
  const rowBg = isDark ? '#0d1117' : '#f8fafc';
  const rowBorder = isDark ? '#1F2937' : '#e2e8f0';

  // Normalise to multi-condition format
  const conditions = config.conditions?.length
    ? config.conditions
    : [{ left: config.left || '', operator: config.operator || 'equals', right: config.right || '' }];
  const combinator = config.combinator || 'and';

  const inputStyle = {
    background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 6,
    padding: '5px 8px', color: text1, fontSize: 12, outline: 'none',
    fontFamily: "'Inter', sans-serif", width: '100%', boxSizing: 'border-box',
  };

  const push = (newConditions, newCombinator) => {
    onUpdate({ ...config, conditions: newConditions, combinator: newCombinator ?? combinator });
  };

  const updateCond = (idx, key, val) => {
    const next = conditions.map((c, i) => i === idx ? { ...c, [key]: val } : c);
    push(next);
  };

  const addCond = () => push([...conditions, { left: '', operator: 'equals', right: '' }]);

  const removeCond = (idx) => {
    const next = conditions.filter((_, i) => i !== idx);
    push(next.length ? next : [{ left: '', operator: 'equals', right: '' }]);
  };

  return (
    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Combinator selector */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: text2, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 6 }}>Combine rules with</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {['and', 'or'].map((opt) => (
            <button
              key={opt}
              onClick={() => push(conditions, opt)}
              style={{
                flex: 1, padding: '6px 0', borderRadius: 6, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
                background: combinator === opt ? '#8B5CF6' : (isDark ? '#1F2937' : '#e2e8f0'),
                color: combinator === opt ? '#fff' : text2,
                transition: 'all 0.15s',
              }}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Condition rows */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {conditions.map((cond, i) => {
          const opDef = CONDITION_OPERATORS.find(o => o.value === cond.operator);
          const isUnary = opDef?.unary ?? false;
          return (
            <div key={i} style={{ background: rowBg, border: `1px solid ${rowBorder}`, borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Row header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: '#8B5CF6', textTransform: 'uppercase' }}>Condition {i + 1}</span>
                {conditions.length > 1 && (
                  <button onClick={() => removeCond(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 2 }}>
                    <X size={12} />
                  </button>
                )}
              </div>
              {/* Left */}
              <input
                style={inputStyle}
                value={cond.left}
                placeholder="{{node_id.field}} or literal value"
                onChange={e => updateCond(i, 'left', e.target.value)}
              />
              {/* Operator */}
              <select
                style={{ ...inputStyle, cursor: 'pointer' }}
                value={cond.operator}
                onChange={e => updateCond(i, 'operator', e.target.value)}
              >
                {CONDITION_OPERATORS.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </select>
              {/* Right (hidden for unary operators) */}
              {!isUnary && (
                <input
                  style={inputStyle}
                  value={cond.right}
                  placeholder="value to compare against"
                  onChange={e => updateCond(i, 'right', e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Add condition */}
      <button
        onClick={addCond}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: 'rgba(139,92,246,0.08)', border: '1px dashed rgba(139,92,246,0.4)',
          borderRadius: 6, padding: '7px 12px', color: '#8B5CF6', fontSize: 12,
          fontWeight: 600, cursor: 'pointer',
        }}
      >
        <Plus size={13} /> Add Condition
      </button>

      {/* Info */}
      <div style={{ fontSize: 11, color: text2, textAlign: 'center', marginTop: 4 }}>
        Passes execution to <code style={{ color: '#10B981', fontWeight: 'bold' }}>TRUE</code> if conditions match, else <code style={{ color: '#EF4444', fontWeight: 'bold' }}>FALSE</code>.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SwitchCase dedicated config UI
// ─────────────────────────────────────────────────────────────────────────────

function SwitchCaseConfig({ config, onUpdate }) {
  const { isDark } = useTheme();

  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#94A3B8' : '#64748b';
  const inputBg = isDark ? '#161B22' : '#ffffff';
  const inputBorder = isDark ? '#30363D' : '#cbd5e1';
  const rowBg = isDark ? '#0d1117' : '#f8fafc';
  const rowBorder = isDark ? '#1F2937' : '#e2e8f0';

  const inputStyle = {
    background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 6,
    padding: '5px 8px', color: text1, fontSize: 12, outline: 'none',
    fontFamily: "'Inter', sans-serif", width: '100%', boxSizing: 'border-box',
  };

  // Normalise cases to rich object format — defined outside component render cycle
  // and memoised so it only re-runs when config.cases actually changes.
  const cases = React.useMemo(() => {
    const normaliseCases = (raw) => {
      if (!raw) return [
        { handle: 'case_a', match_value: '', operator: 'equals' },
        { handle: 'case_b', match_value: '', operator: 'equals' }
      ];
      if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (trimmed.startsWith('[')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              return parsed.map((item, i) =>
                typeof item === 'object' && item !== null
                  ? { handle: item.handle || `case_${i}`, match_value: item.match_value ?? item.value ?? '', operator: item.operator || 'equals' }
                  : { handle: String(item), match_value: String(item), operator: 'equals' }
              );
            }
          } catch { /* fall through */ }
        }
        // Comma-separated plain list
        return trimmed.split(',').map(s => s.trim()).filter(Boolean)
          .map(s => ({ handle: s, match_value: s, operator: 'equals' }));
      }
      if (Array.isArray(raw)) {
        return raw.map((item, i) =>
          typeof item === 'object' && item !== null
            ? { handle: item.handle || `case_${i}`, match_value: item.match_value ?? item.value ?? '', operator: item.operator || 'equals' }
            : { handle: String(item), match_value: String(item), operator: 'equals' }
        );
      }
      return [
        { handle: 'case_a', match_value: '', operator: 'equals' },
        { handle: 'case_b', match_value: '', operator: 'equals' }
      ];
    };
    return normaliseCases(config.cases);
  }, [config.cases]);
  const matchAll = !!config.match_all;

  const push = (newCases, newMatchAll) => {
    onUpdate({ ...config, cases: newCases, match_all: newMatchAll ?? matchAll });
  };

  const updateCase = (idx, key, val) => {
    const next = cases.map((c, i) => i === idx ? { ...c, [key]: val } : c);
    push(next);
  };

  const addCase = () => {
    if (cases.length >= 5) return;
    // Find the highest existing "case_N" number to avoid duplicate names after deletions.
    const existingNums = cases
      .map(c => { const m = c.handle.match(/^case_(\d+)$/); return m ? parseInt(m[1], 10) : 0; })
      .filter(n => n > 0);
    const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : cases.length + 1;
    push([...cases, { handle: `case_${nextNum}`, match_value: '', operator: 'equals' }]);
  };

  const removeCase = (idx) => {
    const next = cases.filter((_, i) => i !== idx);
    push(next.length ? next : [{ handle: 'case_a', match_value: '', operator: 'equals' }]);
  };

  return (
    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 12 }}>

      {/* Switch value */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: text2, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 5 }}>Value to Switch On</label>
        <input
          style={inputStyle}
          value={config.value || ''}
          placeholder="{{node_id.field}} or literal"
          onChange={e => onUpdate({ ...config, value: e.target.value })}
        />
      </div>

      {/* Cases */}
      <div>
        <label style={{ fontSize: 11, fontWeight: 600, color: text2, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 8 }}>Cases ({cases.length}/5)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {cases.map((c, i) => {
            const isDuplicate = cases.some((other, oIdx) => oIdx !== i && other.handle === c.handle);
            return (
              <div key={i} style={{ background: rowBg, border: `1px solid ${isDuplicate ? 'rgba(239, 68, 68, 0.4)' : rowBorder}`, borderRadius: 8, padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: '#A855F7', textTransform: 'uppercase' }}>Case {i + 1}</span>
                  {cases.length > 1 && (
                    <button onClick={() => removeCase(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 2 }}>
                      <X size={12} />
                    </button>
                  )}
                </div>
                {/* Handle name */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                    <span style={{ fontSize: 10, color: text2, whiteSpace: 'nowrap', fontWeight: 600 }}>Handle</span>
                    <input
                      style={{ ...inputStyle, fontFamily: 'monospace', color: isDuplicate ? '#EF4444' : '#A855F7', borderColor: isDuplicate ? '#EF4444' : inputBorder }}
                      value={c.handle}
                      placeholder="edge_name"
                      onChange={e => updateCase(i, 'handle', e.target.value.replace(/\s+/g, '_').toLowerCase())}
                    />
                  </div>
                  {isDuplicate && (
                    <span style={{ fontSize: 9, color: '#EF4444', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, marginTop: 1 }}>
                      ⚠️ Must be unique
                    </span>
                  )}
                </div>
                {/* Operator */}
                <select
                  style={{ ...inputStyle, cursor: 'pointer' }}
                  value={c.operator}
                  onChange={e => updateCase(i, 'operator', e.target.value)}
                >
                  {CONDITION_OPERATORS.filter(o => !o.unary).map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                {/* Match value */}
                <input
                  style={inputStyle}
                  value={c.match_value}
                  placeholder="value to match"
                  onChange={e => updateCase(i, 'match_value', e.target.value)}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Add case */}
      <button
        onClick={addCase}
        disabled={cases.length >= 5}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
          background: cases.length >= 5 ? (isDark ? 'rgba(255,255,255,0.03)' : '#f1f5f9') : 'rgba(168,85,247,0.08)',
          border: cases.length >= 5 ? `1px dashed ${isDark ? '#30363d' : '#cbd5e1'}` : '1px dashed rgba(168,85,247,0.4)',
          borderRadius: 6, padding: '7px 12px',
          color: cases.length >= 5 ? text2 : '#A855F7',
          fontSize: 12,
          fontWeight: 600,
          cursor: cases.length >= 5 ? 'not-allowed' : 'pointer',
          opacity: cases.length >= 5 ? 0.6 : 1,
          transition: 'all 0.2s',
        }}
      >
        <Plus size={13} /> {cases.length >= 5 ? 'Max 5 Cases Reached' : 'Add Case'}
      </button>

      {/* Match All toggle */}
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', padding: '8px 10px', background: matchAll ? 'rgba(168,85,247,0.08)' : rowBg, border: `1px solid ${matchAll ? 'rgba(168,85,247,0.3)' : rowBorder}`, borderRadius: 8 }}>
        <input
          type="checkbox"
          checked={matchAll}
          onChange={e => push(cases, e.target.checked)}
          style={{ accentColor: '#A855F7', width: 14, height: 14 }}
        />
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: matchAll ? '#A855F7' : text1 }}>Match All Cases (Fan-out)</div>
          <div style={{ fontSize: 10, color: text2, marginTop: 1 }}>Execute all matching branches instead of stopping at the first match</div>
        </div>
      </label>

      <div style={{ fontSize: 11, color: text2, textAlign: 'center', marginTop: 4 }}>
        The <code style={{ color: '#94A3B8', fontWeight: 'bold' }}>default</code> branch fires if no cases match.
      </div>
    </div>
  );
}

export default function NodeConfigPanel({ node, catalogTypes, onUpdate, onDelete, onClose, onManageCredentials, workflowId, allNodes = [], allEdges = [] }) {
  const { isDark } = useTheme();
  const [config, setConfig] = useState(node.data.config || {});
  const [credentials, setCredentials] = useState([]);
  const [agents, setAgents] = useState([]);
  const [panelWidth, setPanelWidth] = useState(280);
  const [idCopied, setIdCopied] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [upstreamOpen, setUpstreamOpen] = useState(true);
  const [expandedUpstreamNodes, setExpandedUpstreamNodes] = useState({});
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(280);

  const catalog = catalogTypes.find((t) => t.type === node.data.type);
  const fields = catalog?.fields || [];

  useEffect(() => {
    setConfig(node.data.config || {});
  }, [node.id]);

  useEffect(() => {
    listCredentials()
      .then((res) => setCredentials(res.data))
      .catch(() => { });

    listMarketplaceAgents()
      .then((res) => setAgents(res.data))
      .catch(() => { });
  }, []);

  const handleChange = (name, value) => {
    const updated = { ...config, [name]: value };
    setConfig(updated);
    onUpdate(node.id, updated);
  };

  const handleAgentChange = (name, agentId, agentName) => {
    const updated = { ...config, [name]: agentId, agent_id: agentId, agent_name: agentName };
    setConfig(updated);
    onUpdate(node.id, updated);
  };

  const onMouseDownResize = useCallback((e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = panelWidth;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
  }, [panelWidth]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!isResizing.current) return;
      const delta = startX.current - e.clientX;
      const newWidth = Math.min(520, Math.max(220, startWidth.current + delta));
      setPanelWidth(newWidth);
    };
    const onMouseUp = () => {
      if (isResizing.current) {
        isResizing.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const panelBg = isDark ? '#0d1117' : '#f8fafc';
  const panelBorder = isDark ? '#1F2937' : '#e2e8f0';
  const text1 = isDark ? '#E6EDF3' : '#0f172a';
  const text2 = isDark ? '#94A3B8' : '#64748b';
  const inputBg = isDark ? '#161B22' : '#ffffff';
  const inputBorder = isDark ? '#30363D' : '#cbd5e1';
  const typeBadgeBg = isDark ? 'rgba(248, 114, 23, 0.06)' : 'rgba(248, 114, 23, 0.04)';
  const helpBg = isDark ? 'rgba(248, 114, 23, 0.05)' : 'rgba(248, 114, 23, 0.03)';
  const helpBorder = isDark ? 'rgba(248, 114, 23, 0.15)' : 'rgba(248, 114, 23, 0.1)';

  if (node.data?.type === 'WebhookTrigger') {
    return (
      <WebhookDiscoveryPanel
        node={node}
        workflowId={workflowId}
        onClose={onClose}
        onDelete={onDelete}
        onMouseDownResize={onMouseDownResize}
      />
    );
  }

  return (
    <div style={{
      width: panelWidth,
      flexShrink: 0,
      background: panelBg,
      borderLeft: `1px solid ${panelBorder}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
    }}>
      {/* Resize handle on left edge */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 8,
          cursor: 'ew-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10,
          background: 'transparent',
          borderLeft: '2px solid transparent',
        }}
        onMouseDown={onMouseDownResize}
        title="Drag to resize"
      >
        <GripVertical size={12} color={isDark ? '#374151' : '#cbd5e1'} />
      </div>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: `1px solid ${panelBorder}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Settings2 size={15} color="#8B5CF6" />
          <span style={{ fontSize: 13, fontWeight: 700, color: text1 }}>Configure Node</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {onDelete && (
            <button
              style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 3, display: 'flex', alignItems: 'center', opacity: 0.8 }}
              onClick={() => onDelete(node.id)}
              title="Delete Node"
            >
              <Trash2 size={14} />
            </button>
          )}
          <button style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: text2, padding: 3, display: 'flex', alignItems: 'center' }} onClick={onClose} title="Close Panel">
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Node type badge + full node ID */}
      <div style={{ padding: '8px 14px', background: typeBadgeBg, borderBottom: `1px solid ${panelBorder}`, flexShrink: 0 }}>
        {/* Type row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#F87217' }}>{node.data.type}</span>
        </div>
        {/* Full Node ID row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: isDark ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.04)', border: `1px solid ${isDark ? '#30363D' : '#e2e8f0'}`, borderRadius: 6, padding: '5px 8px' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: isDark ? '#64748B' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', flexShrink: 0 }}>ID</span>
          <span style={{ fontSize: 11, color: isDark ? '#94A3B8' : '#475569', fontFamily: "'JetBrains Mono','Fira Code',monospace", flex: 1, wordBreak: 'break-all', lineHeight: 1.4 }}>
            {node.id}
          </span>
          <button
            onClick={() => {
              navigator.clipboard.writeText(node.id);
              setIdCopied(true);
              setTimeout(() => setIdCopied(false), 2000);
            }}
            title="Copy Node ID"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', color: idCopied ? '#10B981' : (isDark ? '#64748B' : '#94a3b8'), flexShrink: 0, display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
          >
            {idCopied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
      </div>

      {/* Label */}
      <div style={{ padding: '10px 14px 0' }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: text2, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Label</label>
        <input
          style={{ width: '100%', background: inputBg, border: `1px solid ${inputBorder}`, borderRadius: 7, padding: '7px 10px', color: text1, fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
          value={node.data.label || ''}
          placeholder="Node label"
          onChange={(e) => onUpdate(node.id, config, e.target.value)}
        />
      </div>

      {/* Fields / Custom UI */}
      {node.data.type === 'IfCondition' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <IfConditionConfig
            config={config}
            onUpdate={(updated) => {
              setConfig(updated);
              onUpdate(node.id, updated);
            }}
          />
        </div>
      ) : node.data.type === 'SwitchCase' ? (
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <SwitchCaseConfig
            config={config}
            onUpdate={(updated) => {
              setConfig(updated);
              onUpdate(node.id, updated);
            }}
          />
        </div>
      ) : fields.length > 0 ? (
        <div style={{ flex: 1, overflowY: 'auto', padding: '0 0 8px' }}>
          {fields.map((field) => (
            <div key={field.name} style={{ padding: '10px 14px 0' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: text2, marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{field.label}</label>
              <Field
                field={field}
                value={config[field.name]}
                onChange={handleChange}
                onChangeAgent={field.type === 'agent_select' ? handleAgentChange : undefined}
                credentials={credentials}
                agents={agents}
                onManageCredentials={onManageCredentials}
              />
            </div>
          ))}
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <Settings2 size={24} color={isDark ? '#374151' : '#cbd5e1'} />
          <p style={{ color: text2, fontSize: 12, marginTop: 8, textAlign: 'center' }}>
            {node.data.type.includes('Trigger')
              ? 'Trigger nodes have no configuration.'
              : 'No configurable fields for this node.'}
          </p>
        </div>
      )}


      {/* Template help + Upstream Variables */}
      {(() => {
        // Compute upstream nodes for this panel instance
        const upstreamNodeIds = allEdges
          .filter(e => e.target === node.id)
          .map(e => e.source)
          .filter(Boolean);
        const upstreamNodes = upstreamNodeIds
          .map(id => allNodes.find(n => n.id === id))
          .filter(Boolean);

        const copyField = (template) => {
          navigator.clipboard.writeText(template);
          setCopiedField(template);
          setTimeout(() => setCopiedField(''), 2000);
        };

        return (
          <div style={{ margin: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
            {/* Template syntax hint */}
            <div style={{ padding: 10, background: helpBg, border: `1px solid ${helpBorder}`, borderRadius: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 700, color: '#F87217', marginBottom: 4 }}>
                💡 Template Variables
              </div>
              <code style={{ display: 'block', fontSize: 11, color: '#10B981', background: isDark ? 'rgba(16,185,129,0.08)' : 'rgba(16,185,129,0.05)', padding: '3px 7px', borderRadius: 5, marginBottom: 5, fontFamily: 'monospace' }}>
                {'{{node_id.field_name}}'}
              </code>
              <p style={{ fontSize: 11, color: text2, margin: 0, lineHeight: 1.5 }}>
                Reference output from previous nodes using the node ID and field name in double curly braces.
              </p>
            </div>

            {/* Upstream Variables picker */}
            <div style={{ border: `1px solid ${panelBorder}`, borderRadius: 8, overflow: 'hidden' }}>
              {/* Header toggle */}
              <button
                onClick={() => setUpstreamOpen(v => !v)}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', background: isDark ? 'rgba(139,92,246,0.07)' : 'rgba(139,92,246,0.04)', border: 'none', cursor: 'pointer', borderBottom: upstreamOpen ? `1px solid ${panelBorder}` : 'none' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <GitBranch size={13} color="#8B5CF6" />
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B5CF6' }}>Upstream Variables</span>
                  {upstreamNodes.length > 0 && (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: 'rgba(139,92,246,0.15)', color: '#8B5CF6' }}>
                      {upstreamNodes.length}
                    </span>
                  )}
                </div>
                {upstreamOpen ? <ChevronDown size={13} color={text2} /> : <ChevronRight size={13} color={text2} />}
              </button>

              {upstreamOpen && (
                <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                  {upstreamNodes.length === 0 ? (
                    <div style={{ padding: '16px 12px', textAlign: 'center', color: text2, fontSize: 11, lineHeight: 1.5 }}>
                      No upstream nodes connected.<br />
                      <span style={{ opacity: 0.6 }}>Connect a node to this one to see its available output fields here.</span>
                    </div>
                  ) : (
                    upstreamNodes.map(upNode => {
                      const upType = upNode.data?.type || '';
                      const upLabel = upNode.data?.label || upType;
                      const outputFields = NODE_OUTPUT_FIELDS[upType] || [];
                      const isExpanded = expandedUpstreamNodes[upNode.id] !== false; // default open

                      return (
                        <div key={upNode.id} style={{ borderBottom: `1px solid ${panelBorder}` }}>
                          {/* Node header */}
                          <button
                            onClick={() => setExpandedUpstreamNodes(prev => ({ ...prev, [upNode.id]: !isExpanded }))}
                            style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 7, padding: '7px 10px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                          >
                            {isExpanded ? <ChevronDown size={12} color={text2} /> : <ChevronRight size={12} color={text2} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: text1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{upLabel}</div>
                              <div style={{ fontSize: 9, color: '#8B5CF6', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 1 }}>{upType}</div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div style={{ paddingBottom: 6 }}>
                              {/* Node ID row — copy the bare ID */}
                              <div
                                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 28px' }}
                              >
                                <span style={{ flex: 1, fontSize: 10, color: isDark ? '#64748B' : '#94a3b8', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={upNode.id}>
                                  {upNode.id}
                                </span>
                                <button
                                  onClick={() => copyField(upNode.id)}
                                  title="Copy node ID"
                                  style={{ flexShrink: 0, background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)', border: `1px solid ${panelBorder}`, borderRadius: 4, padding: '2px 5px', color: copiedField === upNode.id ? '#10B981' : text2, cursor: 'pointer', fontSize: 9, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}
                                >
                                  {copiedField === upNode.id ? <Check size={10} /> : <Copy size={10} />}
                                  ID
                                </button>
                              </div>

                              {/* Output fields */}
                              {outputFields.length === 0 ? (
                                <div style={{ padding: '4px 28px', fontSize: 10, color: text2, fontStyle: 'italic' }}>No output fields defined.</div>
                              ) : (
                                outputFields.map(field => {
                                  const template = `{{${upNode.id}.${field}}}`;
                                  const isCopied = copiedField === template;
                                  return (
                                    <div
                                      key={field}
                                      style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 10px 3px 28px', borderRadius: 4, margin: '0 4px', transition: 'background 0.15s' }}
                                      onMouseEnter={e => e.currentTarget.style.background = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}
                                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                      <code style={{ flex: 1, fontSize: 10, color: '#10B981', fontFamily: "'JetBrains Mono','Fira Code',monospace", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={template}>
                                        {template}
                                      </code>
                                      <button
                                        onClick={() => copyField(template)}
                                        title={`Copy ${template}`}
                                        style={{ flexShrink: 0, background: isCopied ? 'rgba(16,185,129,0.1)' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'), border: `1px solid ${isCopied ? 'rgba(16,185,129,0.3)' : panelBorder}`, borderRadius: 4, padding: '2px 5px', color: isCopied ? '#10B981' : text2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, transition: 'all 0.2s' }}
                                      >
                                        {isCopied ? <Check size={10} /> : <Copy size={10} />}
                                      </button>
                                    </div>
                                  );
                                })
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}